import React from 'react';
import PropTypes from 'prop-types';
import { IconButton, Input } from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';
import { USER_QUESTION_CHARACTER_LIMIT } from '../../shared/constants';

/**
 * Shared, memoized wrapper around SUI's auto-resizing multiline <Input>.
 *
 * That Input re-measures its height with a forced synchronous reflow
 * (getComputedStyle + reading scrollHeight) on EVERY render. Both drawer inputs
 * live under providers whose context churns often (botLoading / isSubmitting /
 * upload state), so without memoization the textarea re-measures constantly for
 * renders that don't change anything it displays.
 *
 * Memoizing it means it only re-measures when its own value/placeholder/state
 * actually change. For that to hold, callers MUST pass:
 *   - referentially-stable callbacks (useCallback, using a ref for latest state), and
 *   - a memoized `trailingAdornment` node (or null).
 * Everything else here is a primitive, so the memo is not accidentally defeated.
 */
export const ComposerInput = React.memo(function ComposerInput({
  value,
  onChange,
  onKeyDown,
  onKeyUp,
  placeholder,
  ariaLabel,
  inputRef,
  onTouchEnd,
  readOnly = false,
  dimmed = false,
  showClear = false,
  onClear,
  trailingAdornment = null,
}) {
  return (
    <Input
      id="maa-input"
      data-component="ComposerInput"
      inputRef={inputRef}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      fullWidth
      multiline
      // On mobile minimized state the height of the textarea gets set to 0
      // this make sure that there is some height available
      inputProps={{
        style: {
          minHeight: '20px',
          color: dimmed ? 'rgba(0, 0, 0, 0.4)' : undefined,
        },
      }}
      // based on how many lines are created by max character limit
      maxRows={8}
      placeholder={placeholder}
      inputAttributes={{
        maxLength: USER_QUESTION_CHARACTER_LIMIT,
        resize: 'none',
        readOnly,
        'aria-label': ariaLabel,
        // MAG4-853: callers own the tap so focus can apply preventScroll — see
        // .claude/context/drawer-layout.md.
        onTouchEnd,
      }}
      endAdornment={(showClear || trailingAdornment) ? (
        <div className="sui-flex sui-items-center sui-gap-1">
          {showClear && (
            <IconButton
              icon={Close}
              size="small"
              onClick={onClear}
              aria-label="Clear question"
              key="clear-button"
            />
          )}
          {trailingAdornment}
        </div>
      ) : undefined}
    />
  );
});

ComposerInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  // Either ref form: `current` may hold a DOM node, but no PropTypes validator names one
  // without a browser global unavailable during SSR — left unconstrained on purpose.
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  onTouchEnd: PropTypes.func,
  readOnly: PropTypes.bool,
  dimmed: PropTypes.bool,
  showClear: PropTypes.bool,
  onClear: PropTypes.func,
  trailingAdornment: PropTypes.node,
};
